/**
 * Free-tier result copy + presentation logic, ported verbatim from the functioning
 * Replit SPA (pages/results.tsx BOTTLENECK_META / ONE_MOVE, lib/work-fit-questions.ts
 * WF_DIMENSION_LABELS, lib/perspective-content.ts PERSPECTIVE_PATTERN_CONTENT,
 * lib/perspective-questions.ts PERSPECTIVE_PATTERN_LABELS + getPerspectiveIntensityLabel,
 * lib/scoring.ts computeOverallScore / getHpGrade). Paid-report bodies are excluded.
 */

export const BOTTLENECK_META: Record<string, { label: string; explanation: string; action: string }> = {
  avoidance: {
    label: 'Avoidance',
    explanation:
      'You are likely not avoiding because you do not care. You are avoiding because the task carries pressure, uncertainty, or the risk of falling short. The avoidance is a protection response, not a motivation problem.',
    action:
      'Choose the task you are avoiding most and work on it for 10 minutes before checking messages, email, or social media.',
  },
  identityPressure: {
    label: 'Identity Pressure',
    explanation:
      'You may be carrying pressure to appear capable, prepared, or successful, which can make unfinished work feel safer than imperfect work. The standard you are holding yourself to may be creating more friction than clarity.',
    action: 'Finish one small thing at 80% quality and let it be done.',
  },
  focus: {
    label: 'Focus',
    explanation:
      'Your attention is likely pulled in too many directions at once, making it hard to stay with any one thing long enough to build real momentum. This is not a discipline issue. It is a priority architecture issue.',
    action:
      'Pick one task to complete fully before switching to anything else. Close every other tab and context until it is done.',
  },
  recovery: {
    label: 'Recovery & Energy',
    explanation:
      'You may be running on a chronic energy deficit, treating rest as a reward rather than a requirement. Performance that is not recovered from degrades in quality even when effort stays the same.',
    action:
      'Choose one non-negotiable recovery action for today: earlier sleep, a real meal, a walk, or 30 minutes without screens or stimulation.',
  },
  emotionalRegulation: {
    label: 'Emotional Regulation',
    explanation:
      'Stress, friction, or emotional weight may be taking more of your cognitive bandwidth than you realize. When emotional regulation is taxed, decision quality, follow-through, and creative output all suffer first.',
    action:
      'Before your next work block, take 5 minutes to write down what is weighing on you. Getting it out of your head frees space to work.',
  },
  goalClarity: {
    label: 'Goal Clarity',
    explanation:
      'You may be working hard without being fully clear on what you are working toward or why it matters right now. Effort without a clear direction often feels busy but not productive.',
    action:
      "Write one sentence that finishes this: 'The most important thing I am working to complete in the next 30 days is...' Keep it to one thing.",
  },
  isolation: {
    label: 'Isolation & Support',
    explanation:
      'You may be carrying more than you need to alone. High Performers often under-use their network because asking feels like weakness, but isolation compounds every other bottleneck.',
    action:
      'Identify one person who could give perspective, support, or accountability. Send a simple message asking for a quick check-in.',
  },
  overextension: {
    label: 'Overextension',
    explanation:
      'You are likely doing too much, which is quietly eroding the quality of everything. Overextension does not just drain energy. It fragments attention and prevents any single commitment from getting your best.',
    action:
      'Remove one commitment, task, or expectation from your list today. Protecting your capacity is how your next level becomes possible.',
  },
};

export const BOTTLENECK_ONE_MOVE: Record<string, string> = {
  'The Avoidant High Performer':
    'Before the day ends, choose the task you have been avoiding longest. Set a timer for 10 minutes and start without preparing, researching, organizing, or improving the plan.',
  'The Scattered Builder':
    'Before the day ends, write down every active project on your plate. Circle the one that would create the most progress if completed. That is your priority for the next 7 days.',
  'The Overextended High Performer':
    'Before the day ends, remove one commitment, task, or expectation from your list. Your next level will not come from carrying more. It will come from protecting your capacity.',
  'The Identity-Pressured High Performer':
    'Before the day ends, identify one place where you are trying to look capable instead of being honest about what you need. Write the truth privately, then take one action from that truth.',
  'The Isolated Operator':
    'Before the day ends, identify one person who could give perspective, support, or accountability. Send a simple message asking for a quick check-in.',
  'The Under-Recovered High Performer':
    'Before the day ends, choose one recovery action that is non-negotiable: earlier sleep, a real meal, a walk, or 30 minutes without stimulation. Treat recovery as part of performance, not a reward for finishing everything.',
};

export const WF_DIMENSION_LABELS: Record<string, string> = {
  autonomyFit: 'Autonomy Fit',
  structureFit: 'Structure Fit',
  paceFit: 'Pace Fit',
  decisionMakingFit: 'Decision-Making Fit',
  feedbackManagerFit: 'Feedback and Manager Fit',
  cultureClimateFit: 'Culture and Climate Fit',
  energySustainabilityFit: 'Energy and Sustainability Fit',
  valuesTrustFit: 'Values and Trust Fit',
  conflictPoliticsTolerance: 'Low-Politics Fit',
  roleClarityAuthorityFit: 'Role Clarity and Authority Fit',
};

export const PERSPECTIVE_PATTERN_LABELS: Record<string, string> = {
  futureController: 'Future Controller',
  pastReplayer: 'Past Replayer',
  approvalSeeker: 'Approval Seeker',
  comfortProtector: 'Comfort Protector',
  outcomeIdentifier: 'Outcome Identifier',
  negativeForecaster: 'Negative Forecaster',
  selfDebater: 'Self-Debater',
  presentOperator: 'Present Operator',
};

export interface PerspectivePatternContent {
  coreLens: string;
  strength: string;
  cost: string;
  immediateReframe: string;
}

export const PERSPECTIVE_PATTERN_CONTENT: Record<string, PerspectivePatternContent> = {
  'future-controller': {
    coreLens: 'I need certainty before I can relax or move.',
    strength:
      'You notice risks early, prepare seriously, and care about avoiding preventable mistakes. That foresight is a real asset.',
    cost: 'Your mind may keep asking for more certainty than life can actually provide. The cost is anxious anticipation, overplanning, and delay that can outlast the actual risk.',
    immediateReframe:
      'I do not need complete certainty to take the next useful step. I can prepare from the present without trying to control the future.',
  },
  'past-replayer': {
    coreLens: 'This reminds me of what happened before.',
    strength:
      'You learn from experience, recognize patterns quickly, and carry a deep awareness of what has not worked. That reflection has real value.',
    cost: 'Past outcomes can overshadow present possibilities. The cost is rumination, self-protection that no longer fits the situation, and difficulty staying fully present.',
    immediateReframe:
      'The past can inform me without owning the present. I can use reflection without replaying the pain.',
  },
  'approval-seeker': {
    coreLens: 'What will they think, feel, or say?',
    strength:
      'You are genuinely attuned to how your actions land on others. That relational sensitivity and empathy are strengths in any environment that requires trust.',
    cost: "Other people's responses carry more weight in your internal process than you may want. The cost is self-silencing, unstable confidence, and decisions shaped by approval rather than clarity.",
    immediateReframe: "Other people's responses matter, but they do not get to replace my own clarity.",
  },
  'comfort-protector': {
    coreLens: 'Discomfort means something is wrong.',
    strength:
      'You have genuine awareness of your own limits and a strong instinct for pacing and self-protection. That boundary sensitivity is worth keeping.',
    cost: 'Growth consistently requires the discomfort you are trying to avoid. The cost is stalled progress, avoidance, and a narrowing range of situations you are willing to enter.',
    immediateReframe: 'Discomfort is not always danger. Sometimes it is the feeling of growth starting.',
  },
  'outcome-identifier': {
    coreLens: 'The result says something about who I am.',
    strength:
      'You hold high standards and take your work seriously. That care and ownership are real strengths when they stay connected to the work rather than your identity.',
    cost: 'Your confidence becomes conditional on outcomes you cannot always control. The cost is that failure feels personal, success becomes pressure, and self-evaluation never fully settles.',
    immediateReframe: 'A result is feedback. It is not a verdict on who I am.',
  },
  'negative-forecaster': {
    coreLens: 'I need to see what could go wrong first.',
    strength:
      'You detect risk early, plan for contingencies, and rarely get blindsided. That threat awareness has real value in high-stakes environments.',
    cost: 'You may be living inside failure scenarios that are not yet real. The cost is threat scanning, awfulizing, and delayed action caused by a filter that sees more danger than actually exists.',
    immediateReframe: 'Seeing risk is useful. Living inside the worst-case scenario is not.',
  },
  'self-debater': {
    coreLens: 'I need to think this through again before I act.',
    strength:
      'You see complexity, think carefully, and resist oversimplification. That precision and analytical depth are genuinely useful in situations that require thorough thinking.',
    cost: 'The thinking can outlast the usefulness of the thinking. The cost is cognitive fatigue, decision delay, and energy spent in internal negotiation instead of forward movement.',
    immediateReframe:
      'I can think clearly and still move. The next useful step does not require solving the whole thing.',
  },
  'present-operator': {
    coreLens: 'I can reflect, prepare, and act from where I am.',
    strength:
      'You can access the past and the future without being captured by either. That grounded agency and flexible perspective allow consistent action under pressure.',
    cost: 'Present-focus can occasionally tip into under-preparation if it becomes a habit rather than a skill. The working edge is staying honest about when reflection or planning would actually help.',
    immediateReframe: 'Stay present, prepare well, reflect honestly, and keep moving.',
  },
};

// ── Presentation logic (ported from lib/scoring.ts + lib/perspective-questions.ts) ──

export function computeOverallScore(scores: {
  bottleneckScore?: number | null | undefined;
  executionReadinessScore?: number | null | undefined;
  recoveryDeficitScore?: number | null | undefined;
  clarityScore?: number | null | undefined;
}): number {
  return (
    (100 - (scores.bottleneckScore ?? 0)) +
    (scores.executionReadinessScore ?? 0) +
    (100 - (scores.recoveryDeficitScore ?? 0)) +
    (scores.clarityScore ?? 0)
  ) / 4;
}

export function getHpGrade(score: number): { grade: string; label: string } {
  if (score >= 85) return { grade: 'S', label: 'Peak' };
  if (score >= 70) return { grade: 'A', label: 'Strong' };
  if (score >= 55) return { grade: 'B', label: 'Building' };
  if (score >= 40) return { grade: 'C', label: 'Strained' };
  return { grade: 'D', label: 'Depleted' };
}

export function getPerspectiveIntensityLabel(score: number): string {
  if (score < 2.5) return 'Low';
  if (score < 3.25) return 'Mild';
  if (score < 4.0) return 'Elevated';
  return 'Strong';
}

/** Human label for a category/dimension/pattern key, across all three assessments. */
export function labelForKey(key: string): string {
  return (
    BOTTLENECK_META[key]?.label ??
    WF_DIMENSION_LABELS[key] ??
    PERSPECTIVE_PATTERN_LABELS[key] ??
    key
  );
}
